import { useCallback, useEffect, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { FinancialStatementPeriod } from 'common/types/financials';
import { CellChange } from 'components/Spreadsheet/Spreadsheet.types';
import { useFinancials } from 'context/FinancialsContext';
import { IWorkbook } from 'models/interfaces/spreadsheet-types/workbook-types';
import logger from 'utilities/logger';
import { AttributeMappedWidgetDrawer } from './AttributeMappedWidgetDrawer';
import { NEGATIVE_PREFIX } from './types';
import { DocumentReferencesView } from '../../../api';
import WidgetDrawerProps from '../../../components/Drawer/widget-drawer/WidgetDrawer.props';
import SheetAttributeManager from '../../../services/sheet-attribute-manager/SheetAttributeManager';
import { useCellMappingStore } from '../../../store';
import { useAttributeMapperStore } from '../../../store/useAttributeMapperStore';
import { rowConfig as balanceSheetRowConfig } from '../balance-sheet/data';
import { BalanceSheetCell } from '../balance-sheet/types';
import { rowConfig as incomeStatementRowConfig } from '../income-statement/data';

export interface FinancialsWidgetDrawerProps extends WidgetDrawerProps {
  workbook: IWorkbook;
  onTableChange: (cell: any, value: number) => void;
}

const FinancialsWidgetDrawer = ({
  onTableChange,
  workbook,
  spreadsheetNotesProps,
  notesProps,
  documentReferencesProps,
  applicationContext,
  documentApplicationStorageKey,
}: FinancialsWidgetDrawerProps) => {
  const { ...items } = useFinancials();
  const { getCellMapping } = useCellMappingStore();
  const { attributeMapperManager, getAttributeMapperManager } = useAttributeMapperStore();

  // opportunity to memoize this
  const incomeStatementIndex = (alias: string) =>
    incomeStatementRowConfig(false).findIndex(item => item.alias === alias);

  const balanceSheetIndex = (alias: string) => balanceSheetRowConfig(false).findIndex(item => item.alias === alias);

  const pushAttributeMappingsToFinancials = async () => {
    const mapper = getAttributeMapperManager();
    if (!mapper) {
      return;
    }
    const mapping = getCellMapping();
    const evaluatedAttributeData = mapper.calculateValues(mapping);

    const incomeChangeSet: CellChange[] = [];
    const balanceChangeSet: CellChange[] = [];

    Object.keys(evaluatedAttributeData).forEach(columnarMetric => {
      Object.keys(evaluatedAttributeData[columnarMetric]).forEach(rowMetric => {
        const incomeStatementRowIndex = incomeStatementIndex(rowMetric);

        const findCell = (item: BalanceSheetCell) => {
          if (item.alias !== rowMetric) {
            return false;
          }
          if (columnarMetric === 'LTM') {
            return item.isLTM;
          }
          if (columnarMetric === 'NTM') {
            return item.isNTM;
          }
          return item.year === columnarMetric;
        };
        if (incomeStatementRowIndex >= 0) {
          const updatedCell = Object.values(workbook.incomeStatement.cells as BalanceSheetCell[]).find(findCell);
          if (updatedCell) {
            const mappedValue = evaluatedAttributeData[columnarMetric][rowMetric].value;

            incomeChangeSet.push({
              cell: updatedCell,
              value: mappedValue,
            });
          }
          return;
        }

        const balanceSheetRowIndex = balanceSheetIndex(rowMetric);
        if (balanceSheetRowIndex >= 0) {
          const updatedCell = Object.values(workbook.balanceSheet.cells as BalanceSheetCell[]).find(findCell);
          if (updatedCell) {
            const mappedValue = evaluatedAttributeData[columnarMetric][rowMetric].value;

            incomeChangeSet.push({
              cell: updatedCell,
              value: mappedValue,
            });
          }
        }
      });
    });

    const fullChanges = incomeChangeSet.concat(balanceChangeSet);
    fullChanges.forEach(change => {
      onTableChange(change.cell, change.value);
    });

    enqueueSnackbar('Mapping pushed to financials', { variant: 'success' });
  };

  const { periods } = items;

  const filterRows = row => !row.readOnly;
  const mapRowsToOptions = row => ({ label: row.value, value: row.alias });
  const mapNegRowsToOptions = row => ({ label: `-${row.value}`, value: `${NEGATIVE_PREFIX}${row.alias}` });

  const mapPeriodType = (period: FinancialStatementPeriod) => {
    if (period.period_type === undefined) {
      logger.error(`Period type must be defined. Period: ${JSON.stringify(period)}`, undefined);
    } else if (period.period_type < 3) {
      return {
        label: period.year,
        value: period.year,
      };
    } else {
      return {
        label: period.name,
        value: period.name,
      };
    }
  };

  const attributeReferenceConfig = useMemo(() => {
    if (periods) {
      const columns = periods.map(mapPeriodType);
      const balanceSheetRows = balanceSheetRowConfig(false);
      const incomeStatementRows = incomeStatementRowConfig(false);
      const incomeStatementOptions = incomeStatementRows.filter(filterRows).map(mapRowsToOptions);
      const balanceSheetOptions = balanceSheetRows.filter(filterRows).map(mapRowsToOptions);

      const negIncomeStatementOptions = incomeStatementRows.filter(filterRows).map(mapNegRowsToOptions);
      const negBalanceSheetOptions = balanceSheetRows.filter(filterRows).map(mapNegRowsToOptions);
      const rows = [
        { label: 'Income Statement', value: null, isTitle: true },
        ...incomeStatementOptions,
        { label: 'Balance Sheet', value: null, isTitle: true },
        ...balanceSheetOptions,

        { label: '(-)Income Statement', value: null, isTitle: true },
        ...negIncomeStatementOptions,
        { label: '(-)Balance Sheet', value: null, isTitle: true },
        ...negBalanceSheetOptions,
      ];
      return {
        columns,
        rows,
      };
    }
    return { columns: [], rows: [] };
  }, [periods]);

  return (
    <AttributeMappedWidgetDrawer
      documentApplicationStorageKey={documentApplicationStorageKey}
      attributeMapper={attributeMapperManager}
      attributeReferenceConfig={attributeReferenceConfig}
      applicationContext={applicationContext}
      documentReferencesProps={documentReferencesProps}
      notesProps={notesProps}
      spreadsheetNotesProps={spreadsheetNotesProps}
      handlePushingAttributeMappings={pushAttributeMappingsToFinancials}
    />
  );
};

export default FinancialsWidgetDrawer;
